// Infinite horizontal carousel with pinch-to-zoom, keyboard, wheel, and
// touch navigation. Clones one full pairing-cycle of slides before and
// after the originals; when a scroll settles in the clone zone it instantly
// jumps to the equivalent original position.
(function () {
  const PINCH_MIN = 0.5;
  const PINCH_MAX = 4;
  const PINCH_TRANSITION = "transform 500ms var(--motion-ease)";
  const SM_BREAKPOINT = "(min-width: 700px)";
  const SCROLL_DURATION = 1200;
  const SCROLL_EASE = [0.3, 0, 0, 1];
  const WHEEL_THRESHOLD = 80;

  // --- Elements ---

  const carousel = document.querySelector(".pdp-carousel--mobile");
  const wrapper = document.querySelector(".pdp-carousel__inner");
  const track = document.querySelector(".pdp-carousel__track");
  const statusEl = document.querySelector(".pdp-carousel__status");

  if (!track || !wrapper || !carousel) return;

  const slides = Array.from(track.querySelectorAll(".pdp-carousel__slide"));
  const slideCount = slides.length;

  if (slideCount === 0) return;

  // --- Cloning ---

  const setSize = slideCount % 2 === 0 ? slideCount : slideCount * 2;

  const fragment = document.createDocumentFragment();
  for (let i = 0; i < setSize; i++) {
    fragment.appendChild(slides[i % slideCount].cloneNode(true));
  }
  track.insertBefore(fragment, track.firstChild);

  for (let i = 0; i < setSize; i++) {
    track.appendChild(slides[i % slideCount].cloneNode(true));
  }

  const allSlides = track.querySelectorAll(".pdp-carousel__slide");

  // --- State ---

  const PINCH_IDLE = {
    active: false,
    target: null,
    startDist: 0,
    startScale: 1,
    scrollLeft: 0,
  };

  let slideWidth = 0;
  let perView = 1;
  let scrollAnim = null;
  let wheelDelta = 0;
  let touch = { active: false, startX: 0, startY: 0, scroll: 0, locked: null };
  let pinch = { ...PINCH_IDLE };

  // --- Easing ---

  function cubicBezier(x1, y1, x2, y2) {
    const b = (a, c, t) =>
      3 * a * (1 - t) * (1 - t) * t + 3 * c * (1 - t) * t * t + t * t * t;
    return function (p) {
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 20; i++) {
        const t = (lo + hi) / 2;
        if (b(x1, x2, t) < p) lo = t;
        else hi = t;
      }
      return b(y1, y2, (lo + hi) / 2);
    };
  }

  const ease = cubicBezier(...SCROLL_EASE);

  // --- Navigation ---

  function getScrollIndex() {
    return Math.round(wrapper.scrollLeft / slideWidth);
  }

  function scrollToSlide(idx, smooth = true) {
    const target = idx * slideWidth;

    if (scrollAnim) {
      cancelAnimationFrame(scrollAnim);
      scrollAnim = null;
    }

    if (!smooth) {
      wrapper.scrollLeft = target;
      return;
    }

    const start = wrapper.scrollLeft;
    const delta = target - start;
    const t0 = performance.now();

    function step(now) {
      const progress = Math.min((now - t0) / SCROLL_DURATION, 1);
      wrapper.scrollLeft = start + delta * ease(progress);

      if (progress < 1) {
        scrollAnim = requestAnimationFrame(step);
        return;
      }

      scrollAnim = null;
      announce();
      rewindIfNeeded();
    }

    scrollAnim = requestAnimationFrame(step);
  }

  function announce() {
    if (!statusEl) return;
    const idx = getScrollIndex() - setSize;
    const slide = ((idx % slideCount) + slideCount) % slideCount;
    const page = Math.floor(slide / perView) + 1;
    const total = Math.ceil(slideCount / perView);

    statusEl.textContent = `${page} / ${total}`;
  }

  function measure() {
    if (!wrapper.offsetWidth) return;

    const prevIndex = slideWidth ? getScrollIndex() : setSize;

    perView = window.matchMedia(SM_BREAKPOINT).matches ? 2 : 1;
    slideWidth = wrapper.offsetWidth / perView;

    allSlides.forEach((s) => {
      s.style.width = `${slideWidth}px`;
    });

    wrapper.scrollLeft = prevIndex * slideWidth;
    announce();
  }

  function rewindIfNeeded() {
    if (pinch.active || touch.active || scrollAnim) return;
    const idx = getScrollIndex();
    if (idx < setSize) scrollToSlide(idx + setSize, false);
    else if (idx >= setSize * 2) scrollToSlide(idx - setSize, false);
  }

  // --- Pinch-to-zoom ---

  function touchDist(t) {
    return Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY);
  }

  function startPinch(e) {
    const t = e.touches;
    const cx = (t[0].clientX + t[1].clientX) / 2;
    const cy = (t[0].clientY + t[1].clientY) / 2;
    const el = document
      .elementFromPoint(cx, cy)
      ?.closest(".pdp-carousel__image-wrap");

    pinch = {
      active: true,
      target: el,
      startDist: touchDist(t),
      startScale: 1,
      scrollLeft: wrapper.scrollLeft,
    };

    carousel.classList.add("is-pinching");

    // margin-left (not transform) keeps the slide in place without creating a
    // stacking context that would trap the active image below the overlay.
    track.style.marginLeft = `${-pinch.scrollLeft}px`;

    if (el) {
      el.classList.add("is-active");
      pinch.startScale =
        parseFloat(
          el.querySelector(".pdp-carousel__image")?.dataset.pinchScale,
        ) || 1;
    }
  }

  function movePinch(e) {
    e.preventDefault();
    if (!pinch.target) return;

    const scale = Math.max(
      PINCH_MIN,
      Math.min(
        PINCH_MAX,
        (touchDist(e.touches) / pinch.startDist) * pinch.startScale,
      ),
    );
    const img = pinch.target.querySelector(".pdp-carousel__image");
    img.style.transform = `scale(${scale})`;
    img.dataset.pinchScale = String(scale);
  }

  function endPinch() {
    const { target, scrollLeft: savedScroll } = pinch;

    function restore() {
      if (target) {
        target.classList.remove("is-active");
      }
      carousel.classList.remove("is-pinching");
      track.style.marginLeft = "";
      wrapper.scrollLeft = savedScroll;
    }

    if (target) {
      const img = target.querySelector(".pdp-carousel__image");
      img.style.transition = PINCH_TRANSITION;
      img.style.transform = "scale(1)";
      delete img.dataset.pinchScale;

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        img.style.transition = "";
        img.style.transform = "";
        restore();
      };

      img.addEventListener("transitionend", settle, { once: true });
      setTimeout(settle, 600);
    } else {
      restore();
    }

    pinch = { ...PINCH_IDLE };
  }

  // --- Event handlers ---

  function onKeydown(e) {
    if (document.activeElement !== wrapper) return;

    const idx = getScrollIndex();
    const targets = {
      ArrowLeft: idx - perView,
      ArrowRight: idx + perView,
      Home: setSize,
      End: setSize + setSize - perView,
    };

    if (!(e.key in targets)) return;

    scrollToSlide(targets[e.key]);
    e.preventDefault();
  }

  function onWheel(e) {
    if (scrollAnim || pinch.active) return;
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;

    e.preventDefault();
    wheelDelta += e.deltaX;
    if (Math.abs(wheelDelta) < WHEEL_THRESHOLD) return;

    const idx = getScrollIndex();
    scrollToSlide(wheelDelta > 0 ? idx + perView : idx - perView);
    wheelDelta = 0;
  }

  function onTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      startPinch(e);
      return;
    }

    if (e.touches.length !== 1) return;

    if (scrollAnim) {
      cancelAnimationFrame(scrollAnim);
      scrollAnim = null;
    }

    touch = {
      active: true,
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      scroll: wrapper.scrollLeft,
      locked: null,
    };
  }

  function onTouchMove(e) {
    if (e.touches.length === 2 && pinch.active) {
      movePinch(e);
      return;
    }
    if (!touch.active || e.touches.length !== 1) return;

    const dx = e.touches[0].clientX - touch.startX;
    const dy = e.touches[0].clientY - touch.startY;

    if (!touch.locked) {
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        touch.locked = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
      }
      return;
    }

    if (touch.locked === "x") {
      e.preventDefault();
      wrapper.scrollLeft = touch.scroll - dx;
    }
  }

  function onTouchEnd(e) {
    if (pinch.active && e.touches.length < 2) {
      endPinch();
      touch.active = false;
      return;
    }

    if (!touch.active || e.touches.length !== 0) return;

    const wasHorizontal = touch.locked === "x";
    touch.active = false;
    touch.locked = null;

    if (wasHorizontal) {
      const dx = e.changedTouches[0].clientX - touch.startX;
      const raw = wrapper.scrollLeft / slideWidth;
      const idx = dx < 0 ? Math.ceil(raw) : Math.floor(raw);
      scrollToSlide(Math.round(idx / perView) * perView);
    }
  }

  // --- Init ---

  let scrollTimer = null;

  wrapper.addEventListener("scroll", () => {
    announce();
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(rewindIfNeeded, 150);
  });

  wrapper.addEventListener("scrollend", () => {
    clearTimeout(scrollTimer);
    rewindIfNeeded();
  });

  wrapper.addEventListener("touchstart", onTouchStart, { passive: false });
  wrapper.addEventListener("touchmove", onTouchMove, { passive: false });
  window.addEventListener("touchend", onTouchEnd);
  wrapper.addEventListener("keydown", onKeydown);
  wrapper.addEventListener("wheel", onWheel, { passive: false });
  window.addEventListener("resize", measure);

  measure();
})();
